import { MEASUREMENT_EMISSION_FIELDS } from '../reports/environmental/kilnFields.js';

/**
 * Die Matrixzeilen des Emissionsbericht-Imports kommen aus den DATEN
 * (`sourceData.measurements[0].values`, siehe `EmissionReportMatrix.tsx`), nicht mehr aus einer
 * eigenen Ausschlussliste in diesem Modul (Punkt 7, WR-14, 35.1-05). `buildDocument()` gibt jeder
 * Messung bereits alle 22 Katalogschluessel mit; eine Ausschlussliste fuer Beryllium/Selen/Tellur/
 * Zink war deshalb ueberfluessig -- diese vier kommen in `sourceData` nie vor. Beschriftung, Einheit
 * und Gruppenreihenfolge liefert weiterhin das bestehende Formularverzeichnis
 * `MEASUREMENT_EMISSION_FIELDS` (`kilnFields.ts`), KEINE zweite Beschriftungsliste (Lehre aus
 * 18-08). Dass beide Seiten (Backend-Katalog vs. Formularverzeichnis) dieselben Einheiten fuehren,
 * haelt der Vertragstest `emissionFieldCatalogContract.test.ts` zusammen, nicht dieses Modul.
 */

export interface ImportableEmissionField {
  key: string;
  label: string;
  unit: string;
  group: string;
}

export interface ImportableEmissionGroup {
  title: string;
  fields: ImportableEmissionField[];
}

// `MEASUREMENT_EMISSION_FIELDS` fuehrt drei Gruppen mit strukturell leicht unterschiedlichem
// Schema (Einheit auf Gruppen- ODER Feldebene) -- hier generisch als loses Objekt gelesen statt
// eine dritte Kopie der drei Gruppennamen anzulegen.
interface EmissionFieldGroupShape {
  title: string;
  unit?: string;
  fields: ReadonlyArray<{ key: string; label: string; unit?: string }>;
}

const GROUPS = Object.values(MEASUREMENT_EMISSION_FIELDS) as EmissionFieldGroupShape[];

const UNKNOWN_GROUP_TITLE = 'Ohne Formularfeld';

/**
 * Beschriftung und Einheit zu einem Katalogschluessel aus dem Formularverzeichnis. `null`, wenn
 * der Schluessel dort nicht vorkommt (etwa ein aus dem Katalog entfernter oder falsch
 * geschriebener Wert). Einheit: Feld- vor Gruppeneinheit, dieselbe Regel wie in
 * `importableEmissionGroups`.
 */
export function emissionFormField(key: string): { label: string; unit: string } | null {
  for (const group of GROUPS) {
    const field = group.fields.find((candidate) => candidate.key === key);
    if (field) {
      return { label: field.label, unit: field.unit ?? group.unit ?? '' };
    }
  }
  return null;
}

/**
 * Deutsche Feldbeschriftung zu einem Katalogschluessel (Punkt 5, Vorschau-Teil, 35.1-04) --
 * genutzt von `PreviewNotes`, damit die "nicht gelesen"-Liste `HCl` statt `hcl` zeigt. Faellt auf
 * den Rohschluessel zurueck statt zu werfen, wenn `emissionFormField` `null` liefert.
 */
export function emissionFieldLabel(key: string): string {
  return emissionFormField(key)?.label ?? key;
}

/**
 * Leitet die Matrixzeilen aus den tatsaechlich vorhandenen Datenschluesseln ab (Punkt 7, WR-14).
 * Gruppenreihenfolge und Feldreihenfolge folgen `MEASUREMENT_EMISSION_FIELDS`; nur Schluessel aus
 * `keys` werden aufgenommen, leere Gruppen entfallen. Schluessel ohne Formulareintrag (etwa ein
 * geaenderter Backend-Katalog oder ein unbekannter Rohschluessel) landen in der Reihenfolge von
 * `keys` in einer abschliessenden Gruppe `Ohne Formularfeld` mit `label = key` und `unit = ''` --
 * kein `undefined`, kein Absturz beim Klick.
 */
export function importableEmissionGroups(keys: readonly string[]): ImportableEmissionGroup[] {
  const keySet = new Set(keys);
  const groups: ImportableEmissionGroup[] = [];

  for (const group of GROUPS) {
    const fields: ImportableEmissionField[] = [];
    for (const field of group.fields) {
      if (!keySet.has(field.key)) continue;
      fields.push({
        key: field.key,
        label: field.label,
        unit: field.unit ?? group.unit ?? '',
        group: group.title,
      });
    }
    if (fields.length > 0) {
      groups.push({ title: group.title, fields });
    }
  }

  const knownKeys = new Set(GROUPS.flatMap((group) => group.fields.map((field) => field.key)));
  const unknownFields: ImportableEmissionField[] = keys
    .filter((key) => !knownKeys.has(key))
    .map((key) => ({ key, label: key, unit: '', group: UNKNOWN_GROUP_TITLE }));

  if (unknownFields.length > 0) {
    groups.push({ title: UNKNOWN_GROUP_TITLE, fields: unknownFields });
  }

  return groups;
}
